// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Data tab's pins (docs/AYNI_TOOLS_DATA_PLAN.md, Phase 3): at
 * most six points on the globe, each with its colour, its latest reading
 * and its series across the dataset's window.
 *
 * Pure state. The caller owns the clock: it calls `sample` on a timer with
 * what the globe is showing, and the store reads only when the frame or the
 * pins changed since the last read, so a paused globe costs nothing. Moving
 * a pin starts its series again, since the old one described another place;
 * a new dataset starts every series again for the same reason, but the pins
 * stay where the viewer put them.
 *
 * The browser's Data tab drives it today; the VR and handheld panels
 * (Phases 4 and 5) are meant to edit the same store.
 */

import { createSeries, extendSeries, recordSample, type MeasurementSeries } from './series'
import { nextMeasurementColor } from './colors'
import type { SurfaceMode } from './surfaceMask'

/** AYNI (owner, 2026-10-09): six, in every environment (was four). */
export const MAX_PINS = 6

/** AYNI (owner, 2026-10-09): the longest name a pin may be given. */
export const PIN_NAME_MAX = 32

/** A name as typed, tidied: spaces collapsed, cut to the limit; null when nothing is left (the pin goes back to its own name). */
export function cleanPinName(raw: string | null | undefined): string | null {
  const name = (raw ?? '').replace(/\s+/g, ' ').trim().slice(0, PIN_NAME_MAX).trim()
  return name || null
}

/** What the probe says at a point: a value (or no data there), or null when there is nothing to read. */
export interface PinReading {
  readonly value: number
  readonly noData: boolean
}

/** Set on the pins "Mark highest & lowest" placed, so pressing it again replaces them. */
export type PinTag = 'max' | 'min'

export interface Pin {
  readonly id: string
  readonly color: string
  lat: number
  lon: number
  tag: PinTag | null
  /**
   * AYNI (owner, 2026-10-18): a tagged pin that moves to its frame's
   * highest (lowest) value on every frame read, its series that value frame
   * by frame and each slot where it was read (`followExtremes.ts`). False:
   * it stays where it was marked.
   */
  follow: boolean
  /**
   * AYNI (owner, 2026-10-09): the name the viewer gave it, shown wherever
   * the pin is named (the Data tab, the graph, the globes, the headset, the
   * CSV); null: its own ("Pin 2", "Highest").
   */
  name: string | null
  /** The latest reading, or null when the point has nothing to read (outside the data, or no frame yet). */
  reading: PinReading | null
  /** Across the dataset's window; null for a still image or before the first read. */
  series: MeasurementSeries | null
  /**
   * AYNI (owner, 2026-10-22): a room lead's pin shown here (`adopt`), by the
   * lead's colour (its identity there; this pin's own colour may differ when
   * the viewer's own pin has the lead's). Absent: this viewer's own pin.
   */
  shared?: { color: string }
}

/** The frame on the globe, as the sampler needs it. */
export interface PinFrame {
  /** Changes whenever the frame does (the video playhead, or 'static'). */
  readonly id: string
  /** Changes with the dataset, so series restart. */
  readonly datasetKey: string
  /** Where the frame sits, and the window around it; nulls for a still image. */
  readonly timeMs: number | null
  readonly startMs: number | null
  readonly endMs: number | null
  /** Slots for a new series: one per frame when the timeline says how many. */
  readonly slots?: number
}

export type PinReader = (lat: number, lon: number) => PinReading | null

/**
 * AYNI: where the frame's highest and lowest values are, nearest where the
 * following pins stand (`near`); null when the frame cannot be searched now
 * (nothing to read, or too soon after the last search: `LocateBudget`).
 */
export type ExtremesLocator = (
  near: Partial<Record<PinTag, { lat: number; lon: number }>>,
) => Partial<Record<PinTag, { lat: number; lon: number }>> | null

export class PinStore {
  /**
   * @param limitOf the most pins the plan allows now (Plus 2, owner,
   *   2026-10-06); never above `MAX_PINS`. Pins already down stay when it
   *   drops; no new one can be added until there are fewer.
   */
  constructor(private readonly limitOf: () => number = () => MAX_PINS) {}

  private pins: Pin[] = []
  private readonly listeners = new Set<() => void>()
  private counter = 0
  private lastFrameId: string | null = null
  private datasetKey: string | null = null
  /** Set when the pins changed, so the next `sample` reads even on the same frame. */
  private dirty = false
  private tracks = false
  private searchOn: SurfaceMode = 'all'

  list(): readonly Pin[] {
    return this.pins
  }

  /** AYNI (owner, 2026-10-18): whether the following pins draw the track they left. One switch for both. */
  get trackShown(): boolean {
    return this.tracks
  }

  setTrackShown(on: boolean): void {
    if (this.tracks === on) return
    this.tracks = on
    this.emit()
  }

  /**
   * AYNI (owner, 2026-10-18): where the highest and lowest are looked for:
   * everywhere, on land only or at sea only (`surfaceMask.ts`). One choice
   * for the browser, the headset and the chatbot.
   */
  get surface(): SurfaceMode {
    return this.searchOn
  }

  /**
   * Look for the extremes somewhere else. The following pins look again on
   * the next read, their series started again (it was another extreme);
   * pins that stay are not moved. Returns whether anything changed.
   */
  setSurface(mode: SurfaceMode): boolean {
    if (this.searchOn === mode) return false
    this.searchOn = mode
    for (const pin of this.pins) {
      if (!pin.follow) continue
      pin.reading = null
      pin.series = null
    }
    this.changed()
    return true
  }

  /** Read the pins again on the next `sample`, the same frame too (a land and sea map has come in). */
  refresh(): void {
    this.dirty = true
  }

  /** Whether any pin follows an extreme. */
  get following(): boolean {
    return this.pins.some((p) => p.follow)
  }

  /** Whether any pin marks an extreme, following or not. */
  get marked(): boolean {
    return this.pins.some((p) => p.tag !== null)
  }

  /**
   * AYNI (owner, 2026-10-18): the highest and lowest pins follow the data,
   * or stay where they stand now ("Freeze here"). Either way their series
   * starts again: it described one thing, and now describes another.
   * Returns whether anything changed.
   */
  setFollow(on: boolean): boolean {
    let changed = false
    for (const pin of this.pins) {
      if (pin.tag === null || pin.follow === on) continue
      pin.follow = on
      pin.reading = null
      pin.series = null
      changed = true
    }
    if (changed) this.changed()
    return changed
  }

  /** The most pins this store takes now. */
  get limit(): number {
    return Math.max(0, Math.min(MAX_PINS, Math.floor(this.limitOf())))
  }

  get full(): boolean {
    return this.pins.length >= this.limit
  }

  /** Drop a pin; null when the store is full. */
  add(lat: number, lon: number, tag: PinTag | null = null, follow = false): Pin | null {
    if (this.full) return null
    const pin: Pin = {
      id: `pin-${++this.counter}`,
      color: nextMeasurementColor(this.pins.map((p) => p.color)),
      lat,
      lon,
      tag,
      follow: tag !== null && follow,
      name: null,
      reading: null,
      series: null,
    }
    this.pins.push(pin)
    this.changed()
    return pin
  }

  move(id: string, lat: number, lon: number): void {
    const pin = this.pins.find((p) => p.id === id)
    if (!pin) return
    pin.lat = lat
    pin.lon = lon
    // A moved pin is somewhere else, and so is no longer the extreme it marked.
    pin.tag = null
    pin.follow = false
    pin.reading = null
    pin.series = null
    this.changed()
  }

  /** AYNI: give a pin a name, or its own back with an empty one. It stays where it is and keeps its series. */
  rename(id: string, name: string | null): void {
    const pin = this.pins.find((p) => p.id === id)
    const next = cleanPinName(name)
    if (!pin || pin.name === next) return
    pin.name = next
    this.emit()
  }

  remove(id: string): void {
    const before = this.pins.length
    this.pins = this.pins.filter((p) => p.id !== id)
    if (this.pins.length !== before) this.changed()
  }

  clear(): void {
    if (!this.pins.length) return
    this.pins = []
    this.changed()
  }

  /**
   * Put the highest and lowest pins where given, replacing any placed by an
   * earlier press. Each lands only while there is room; returns how many did.
   * AYNI (owner, 2026-10-18): they follow the data unless `follow` is false
   * (a classified dataset, whose classes have no highest).
   */
  markExtremes(points: ReadonlyArray<{ lat: number; lon: number; tag: PinTag }>, follow = true): number {
    this.pins = this.pins.filter((p) => p.tag === null)
    let placed = 0
    for (const p of points) if (this.add(p.lat, p.lon, p.tag, follow)) placed++
    this.changed()
    return placed
  }

  /**
   * AYNI (owner, 2026-10-22): show a room lead's pins. They are kept apart
   * as shared pins, matched by the lead's colour: one the lead still has
   * keeps its series unless it moved or began or stopped following; its
   * name and tag follow the lead's. A following pin keeps the place this
   * device found for it, unless this device has not read it, where it takes
   * the lead's. `keepOwn` (a meeting that lends the data tools) keeps this
   * viewer's own pins after the lead's, each shared pin then taking a colour
   * no own pin has; without it the viewer's own pins go (always follow). The
   * plan's limit does not apply: watching someone's pins is free. Returns
   * whether anything changed.
   */
  adopt(
    list: ReadonlyArray<{ lat: number; lon: number; color: string; name: string | null; tag: PinTag | null; follow: boolean }>,
    opts: { keepOwn?: boolean } = {},
  ): boolean {
    let changed = false
    let moved = false
    const own = opts.keepOwn ? this.pins.filter((p) => !p.shared) : []
    const next: Pin[] = []
    for (const want of list.slice(0, MAX_PINS)) {
      if (next.some((p) => p.shared!.color === want.color)) continue
      const follow = want.tag !== null && want.follow
      const had = this.pins.find((p) => p.shared?.color === want.color)
      if (!had) {
        const taken = [...own, ...next].map((p) => p.color)
        const color = taken.includes(want.color) ? nextMeasurementColor(taken) : want.color
        next.push({ id: `pin-${++this.counter}`, color, lat: want.lat, lon: want.lon, tag: want.tag, follow, name: cleanPinName(want.name), reading: null, series: null, shared: { color: want.color } })
        changed = moved = true
        continue
      }
      const away = Math.abs(had.lat - want.lat) > 1e-4 || Math.abs(had.lon - want.lon) > 1e-4
      if (had.follow !== follow || had.tag !== want.tag) {
        had.tag = want.tag
        had.follow = follow
        had.lat = want.lat
        had.lon = want.lon
        had.reading = null
        had.series = null
        changed = moved = true
      } else if (away && (!follow || had.reading === null)) {
        had.lat = want.lat
        had.lon = want.lon
        if (!follow) {
          had.reading = null
          had.series = null
        }
        changed = moved = true
      }
      const name = cleanPinName(want.name)
      if (had.name !== name) {
        had.name = name
        changed = true
      }
      next.push(had)
    }
    next.push(...own)
    if (next.length !== this.pins.length || next.some((p, i) => p !== this.pins[i])) changed = moved = true
    this.pins = next
    if (moved) this.changed()
    else if (changed) this.emit()
    return changed
  }

  /** AYNI: take the room lead's pins off, leaving this viewer's own. */
  dropShared(): void {
    const before = this.pins.length
    this.pins = this.pins.filter((p) => !p.shared)
    if (this.pins.length !== before) this.changed()
  }

  /** AYNI: the lead's pins become this viewer's own (it leads the room now). */
  keepShared(): void {
    if (!this.pins.some((p) => p.shared)) return
    for (const pin of this.pins) delete pin.shared
    this.emit()
  }

  /** AYNI: the pins as they are now, to put back later (`restore`): a room's follower keeps its own while it shows the lead's. */
  snapshot(): Pin[] {
    // Copies: `adopt` changes the pins it keeps in place.
    return this.pins.map((pin) => ({ ...pin }))
  }

  /** AYNI: put back pins taken with `snapshot`, read afresh (the dataset may have changed meanwhile). */
  restore(pins: readonly Pin[]): void {
    this.pins = [...pins].slice(0, MAX_PINS)
    for (const pin of this.pins) {
      pin.reading = null
      pin.series = null
    }
    this.changed()
  }

  /**
   * Read every pin at the frame on the globe, when the frame, the dataset or
   * the pins changed since the last read. Returns whether anything was read.
   *
   * AYNI (owner, 2026-10-18): the following pins move to the frame's
   * extremes first (`locate`), and each slot keeps where it was read. When
   * the frame cannot be searched now they are not read on it at all (their
   * slot stays unvisited for a later lap), and the next call tries again.
   */
  sample(frame: PinFrame | null, read: PinReader, locate?: ExtremesLocator): boolean {
    if (!frame || this.pins.length === 0) return false
    const newDataset = frame.datasetKey !== this.datasetKey
    if (!newDataset && !this.dirty && frame.id === this.lastFrameId) return false
    this.datasetKey = frame.datasetKey
    this.lastFrameId = frame.id
    this.dirty = false
    const timed = frame.timeMs !== null && frame.startMs !== null && frame.endMs !== null && frame.endMs > frame.startMs
    const followers = this.pins.filter((p) => p.follow && p.tag !== null)
    let found: ReturnType<ExtremesLocator> = null
    if (followers.length && locate) {
      const near: Partial<Record<PinTag, { lat: number; lon: number }>> = {}
      for (const p of followers) near[p.tag!] = { lat: p.lat, lon: p.lon }
      found = locate(near)
    }
    let missed = false
    for (const pin of this.pins) {
      if (newDataset) pin.series = null
      // Without a locator the following pins are read where they stand.
      const moves = !!locate && pin.follow && pin.tag !== null
      const at = moves ? found?.[pin.tag!] ?? null : null
      if (moves) {
        if (!at) {
          missed = true
          continue
        }
        pin.lat = at.lat
        pin.lon = at.lon
      }
      pin.reading = read(pin.lat, pin.lon)
      if (!timed) {
        pin.series = null
        continue
      }
      if (!pin.series || pin.series.startMs !== frame.startMs) pin.series = createSeries(frame.startMs!, frame.endMs!, frame.slots)
      // AYNI: a window that gains frames gains slots (one per frame), keeping what was read.
      else if (frame.endMs! > pin.series.endMs || (frame.slots !== undefined && frame.slots !== pin.series.slots.length)) {
        pin.series = extendSeries(pin.series, Math.max(frame.endMs!, pin.series.endMs), frame.slots ?? pin.series.slots.length)
      }
      // Nothing to read (no frame decoded, or outside a regional dataset)
      // leaves the slot unvisited rather than claiming the instrument saw nothing.
      if (pin.series && pin.reading) {
        recordSample(pin.series, frame.timeMs!, pin.reading.noData ? null : pin.reading.value, at ? { lat: at.lat, lon: at.lon } : undefined)
      }
    }
    // Searched later: the same frame is read again on the next call.
    if (missed) this.dirty = true
    this.emit()
    return true
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private changed(): void {
    this.dirty = true
    this.emit()
  }

  private emit(): void {
    for (const fn of this.listeners) fn()
  }
}
